/**
 * Featured brands markup builder — the storefront's copy of the designer's
 * `brandsMarkup.js`, so the published rail and the canvas preview cannot drift.
 *
 * A horizontal rail of brand tiles on the secondary surface, matching SP-main's
 * `brands:` renderer and the shared `.bb-card` tile.
 *
 * The whole tile is the link: no "Shop brand" button, because a row of eight
 * tiles each carrying its own CTA reads as eight competing actions.
 */

import { escapeHtml } from '../../utilities/helper';
import { copyOf, accentTextOf } from './bannerMarkup';

/** Seeds from SP-main's `storefrontSeedText('brands')`, so a cleared field
 *  still reads as a real section. Same strings the panel shows as placeholders. */
export const BRANDS_SEED = {
  title: 'Brands we trust',
  /* The shipping designer uses this shorter line as both the panel's placeholder
     and its render fallback, and the two have to agree. The longer line in this
     workspace's SP-main copy is the older reference. */
  subtitle: 'The makers behind what we recommend.',
};

/* The rail also carries the editor's legacy hook classes (.cards-scroller,
   .scroll-left, .scroll-right), which `attachScrollButtons` binds to — the same
   helper the products and collections rails use. */
const ARROW_PREV = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>`;
const ARROW_NEXT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>`;

/* Palette and hash copied verbatim from the portal's BrandCard and the
   designer's brandsMarkup, so a brand wears the same ring colour on Browse
   brands, in the designer and here. Hashing the name means nothing is stored and
   two brands never swap colours when the list reorders.

   The colour is written straight onto the logo span rather than as a `--bc`
   custom property: GrapesJS re-parses inline styles and drops custom properties,
   so the designer's rings all fell back to grey. */
const BRAND_COLORS = [
  '#2b5137', '#466a85', '#8a5035', '#5a4f7c', '#755f38',
  '#3d635a', '#6f4859', '#4f5e3b', '#7a4242', '#3a5f6e',
];

const brandColorOf = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BRAND_COLORS[Math.abs(hash) % BRAND_COLORS.length];
};

/** One initial on the brand's own colour when there is no logo — a broken <img>
 *  on a brand tile reads as a broken store. */
const initialOf = (name) => (String(name || 'B').trim().charAt(0) || 'B').toUpperCase();

/* The designer only ever links `/brands/:id`, having no vendor routes, but the
   storefront does — a multi-brand vendor reached through the plain brand route
   lands on the wrong page. */
const hrefOf = (brand) => {
  if (!brand?.id) return '#';
  const id = encodeURIComponent(brand.id);
  return brand.type === 'vendor' && brand.vendor_id
    ? `/brands/${id}/vendor/${encodeURIComponent(brand.vendor_id)}`
    : `/brands/${id}`;
};

const tileHtml = (brand) => {
  const name = String(brand?.name || '').trim();
  const logo = String(brand?.logo || '').trim();
  const color = brandColorOf(name);

  const mark = logo
    ? `<img src="${escapeHtml(logo)}" alt="" loading="lazy">`
    : escapeHtml(initialOf(name));

  return (
    `<a class="bb-card sf-bcard" href="${escapeHtml(hrefOf(brand))}" aria-label="${escapeHtml(name)}">` +
    `<span class="bb-logo" style="border-color:${color};background-color:${color}">${mark}</span>` +
    `<span class="bb-name">${escapeHtml(name)}</span>` +
    `</a>`
  );
};

/**
 * @param {{ title?: string, subtitle?: string, visible?: boolean }} block
 * @param {Array<{id: *, name: string, logo: string}>} brands  resolved brand records
 * @param {{ headingFont?: string, themeColor?: string }} options
 * @returns {string} markup, or '' when there is nothing to show
 */
export const buildBrandsHtml = (block = {}, brands = [], options = {}) => {
  const { headingFont, themeColor } = options;

  const list = Array.isArray(brands) ? brands.filter(Boolean) : [];
  // No brands picked yet, so there is no section — not an empty frame, and not
  // the canvas's "Select brands to feature" prompt.
  if (!list.length || block.visible === false) return '';

  const heading = copyOf(block.title) || BRANDS_SEED.title;
  const sub = copyOf(block.subtitle) || BRANDS_SEED.subtitle;
  const headFont = headingFont ? `font-family:${headingFont};` : '';

  return `<section class="sf-sec alt sf-brands-sec">
  <div class="sf-in">
    <div class="sf-sh">
      <div>
        <h2 style="${headFont}">${escapeHtml(heading)}</h2>
        <div class="sub">${escapeHtml(sub)}</div>
      </div>
      <a class="sf-seeall" href="/all-brands" style="color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#2C2F32')}">View all brands &rarr;</a>
    </div>
    <div class="sf-railwrap">
      <div class="sf-arrows">
        <button class="sf-arr prev scroll-left" type="button" aria-label="Scroll back">${ARROW_PREV}</button>
        <button class="sf-arr next scroll-right" type="button" aria-label="Scroll forward">${ARROW_NEXT}</button>
      </div>
      <div class="sf-rail cards-scroller">${list.map(tileHtml).join('')}</div>
    </div>
  </div>
</section>`;
};

export default buildBrandsHtml;
